"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FileTree, FileTreeFile } from "@/components/ai-elements/file-tree";
import { Input } from "@/components/ui/input";
import { ContentSkeleton, ListSkeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { MarkdownPreviewContent } from "@/components/markdown-preview";
import type { FileManagerTreeNode } from "@/hooks/use-file-manager-tree";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import type { DbTeam } from "@/types/db";
import { useEffect, useRef, useState } from "react";

type FileChunk = { id: string; content: string; tokens: number };

export const ChunksDialog = ({
  node,
  open,
  onOpenChange,
}: {
  node: FileManagerTreeNode | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const [chunks, setChunks] = useState<FileChunk[]>([]);
  const [loadingChunks, setLoadingChunks] = useState(false);
  const [activeChunk, setActiveChunk] = useState(0);
  const chunkRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (!open || !node) return;
    function load() {
      setLoadingChunks(true);
      setActiveChunk(0);
      supabase
        .from("file_items")
        .select("id, content, tokens")
        .eq("file_id", node!.entityId)
        .order("created_at", { ascending: true })
        .then(({ data, error }) => {
          if (!error && data) setChunks(data as FileChunk[]);
          setLoadingChunks(false);
        });
    }
    load();
  }, [open, node]);

  const scrollToChunk = (i: number) => {
    setActiveChunk(i);
    chunkRefs.current[i]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] w-[70vw] max-w-[70vw] flex-col gap-0 p-0 sm:max-w-[70vw]">
        <DialogHeader className="shrink-0 border-b border-border px-6 py-4">
          <DialogTitle className="wrap-break-word text-base leading-snug">
            {node?.name ?? "Chunks"}
          </DialogTitle>
          {chunks.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {chunks.length} chunk{chunks.length === 1 ? "" : "s"} ·{" "}
              {chunks.reduce((s, c) => s + c.tokens, 0).toLocaleString()} tokens total
            </p>
          )}
        </DialogHeader>
        <div className="flex min-h-0 flex-1 overflow-hidden">
          {/* Sidebar tree */}
          <div className="flex w-40 shrink-0 flex-col overflow-y-auto border-r border-border bg-muted/30">
            {loadingChunks ? (
              <ListSkeleton rows={3} compact />
            ) : chunks.length === 0 ? (
              <p className="px-3 py-4 text-xs text-muted-foreground">No chunks.</p>
            ) : (
              <FileTree
                className="h-full rounded-none border-0 bg-transparent font-sans"
                selectedPath={chunks[activeChunk]?.id}
                onSelect={(id) => {
                  const i = chunks.findIndex((c) => c.id === id);
                  if (i !== -1) scrollToChunk(i);
                }}
              >
                {chunks.map((chunk, i) => (
                  <FileTreeFile key={chunk.id} path={chunk.id} name={`#${i + 1} · ${chunk.tokens} tok`} />
                ))}
              </FileTree>
            )}
          </div>
          {/* Continuous scroll content */}
          <div className="min-w-0 flex-1 overflow-y-auto px-4 sm:px-6 py-4">
            {loadingChunks ? (
              <ContentSkeleton />
            ) : chunks.length === 0 ? (
              <p className="text-sm text-muted-foreground">No chunks found.</p>
            ) : (
              <div>
                {chunks.map((chunk, i) => (
                  <div
                    key={chunk.id}
                    ref={(el) => { chunkRefs.current[i] = el; }}
                  >
                    {i > 0 && <div className="my-4 border-t border-border" />}
                    <MarkdownPreviewContent
                      mode="preview"
                      value={chunk.content}
                      previewClassName="min-h-0 max-h-none border-0 bg-transparent p-0 h-auto"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const MoveTeamDialog = ({
  node,
  teams,
  open,
  onOpenChange,
  onSubmit,
}: {
  node: FileManagerTreeNode | null;
  teams: DbTeam[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (node: FileManagerTreeNode, targetTeamId: string) => Promise<void>;
}) => {
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset selection when dialog reopens
  useEffect(() => {
    if (!open) return;
    function reset() { setSelectedTeamId(null); }
    reset();
  }, [open]);

  const availableTeams = teams.filter((w) => w.id !== node?.teamId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Move to team</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Select a target team to move{" "}
          <span className="font-medium text-foreground">{node?.name}</span>. A request
          will be sent to the team owner for approval.
        </p>
        {availableTeams.length === 0 ? (
          <p className="text-sm text-muted-foreground">No other teams available.</p>
        ) : (
          <div className="space-y-2">
            {availableTeams.map((w) => (
              <button
                key={w.id}
                type="button"
                className={cn(
                  "w-full rounded-lg border px-4 py-2.5 text-left text-sm transition-colors",
                  selectedTeamId === w.id
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border bg-card text-foreground hover:bg-accent"
                )}
                onClick={() => setSelectedTeamId(w.id)}
              >
                {w.name}
              </button>
            ))}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            disabled={!selectedTeamId || submitting}
            onClick={async () => {
              if (!node || !selectedTeamId) return;
              setSubmitting(true);
              await onSubmit(node, selectedTeamId);
              setSubmitting(false);
              onOpenChange(false);
            }}
          >
            {submitting ? "Sending\u2026" : "Send request"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const BulkMoveTeamDialog = ({
  count,
  currentTeamId,
  teams,
  open,
  onOpenChange,
  onSubmit,
}: {
  count: number;
  currentTeamId: string | null;
  teams: DbTeam[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (targetTeamId: string) => Promise<void>;
}) => {
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const availableTeams = teams.filter((w) => w.id !== currentTeamId);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (v) setSelectedTeamId(null); onOpenChange(v); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Move to team</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Select a target team to move{" "}
          <span className="font-medium text-foreground">{count} file{count === 1 ? "" : "s"}</span>.
          A request will be sent to the team owner for approval.
        </p>
        {availableTeams.length === 0 ? (
          <p className="text-sm text-muted-foreground">No other teams available.</p>
        ) : (
          <div className="space-y-2">
            {availableTeams.map((w) => (
              <button
                key={w.id}
                type="button"
                className={cn(
                  "w-full rounded-lg border px-4 py-2.5 text-left text-sm transition-colors",
                  selectedTeamId === w.id
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border bg-card text-foreground hover:bg-accent"
                )}
                onClick={() => setSelectedTeamId(w.id)}
              >
                {w.name}
              </button>
            ))}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            disabled={!selectedTeamId || submitting}
            onClick={async () => {
              if (!selectedTeamId) return;
              setSubmitting(true);
              await onSubmit(selectedTeamId);
              setSubmitting(false);
              onOpenChange(false);
            }}
          >
            {submitting ? "Sending…" : "Send request"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const BulkMoveCollectionDialog = ({
  count,
  collections,
  teamNodeId,
  open,
  onOpenChange,
  onSubmit,
}: {
  count: number;
  collections: FileManagerTreeNode[];
  teamNodeId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (targetNodeId: string) => Promise<void>;
}) => {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const options = [
    ...(teamNodeId ? [{ id: teamNodeId, name: "No collection (team root)" }] : []),
    ...collections.map((c) => ({ id: c.id, name: c.name })),
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (v) setSelectedNodeId(null); onOpenChange(v); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Move to collection</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Select a destination for{" "}
          <span className="font-medium text-foreground">{count} file{count === 1 ? "" : "s"}</span>.
        </p>
        {options.length === 0 ? (
          <p className="text-sm text-muted-foreground">No collections available.</p>
        ) : (
          <div className="space-y-2">
            {options.map((opt) => (
              <button
                key={opt.id}
                type="button"
                className={cn(
                  "w-full rounded-lg border px-4 py-2.5 text-left text-sm transition-colors",
                  selectedNodeId === opt.id
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border bg-card text-foreground hover:bg-accent"
                )}
                onClick={() => setSelectedNodeId(opt.id)}
              >
                {opt.name}
              </button>
            ))}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            disabled={!selectedNodeId || submitting}
            onClick={async () => {
              if (!selectedNodeId) return;
              setSubmitting(true);
              await onSubmit(selectedNodeId);
              setSubmitting(false);
              onOpenChange(false);
            }}
          >
            {submitting ? "Moving…" : "Move"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const CreateCollectionDialog = ({
  open,
  onOpenChange,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (name: string) => Promise<void>;
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-sm">
      <DialogHeader>
        <DialogTitle>Create collection</DialogTitle>
      </DialogHeader>
      {open && (
        <CreateCollectionForm
          onSubmit={onSubmit}
          onCancel={() => onOpenChange(false)}
        />
      )}
    </DialogContent>
  </Dialog>
);

const CreateCollectionForm = ({
  onSubmit,
  onCancel,
}: {
  onSubmit: (name: string) => Promise<void>;
  onCancel: () => void;
}) => {
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setSubmitting(true);
        await onSubmit(name.trim());
        setSubmitting(false);
        onCancel();
      }}
      className="flex flex-col gap-4"
    >
      <Input
        placeholder="Collection name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoFocus
        disabled={submitting}
      />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={!name.trim() || submitting}>
          {submitting ? "Creating…" : "Create"}
        </Button>
      </div>
    </form>
  );
};

export const RenameCollectionDialog = ({
  node,
  open,
  onOpenChange,
  onSubmit,
}: {
  node: FileManagerTreeNode | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (node: FileManagerTreeNode, name: string) => Promise<void>;
}) => {
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    Promise.resolve().then(() => setName(node?.name ?? ""));
  }, [open, node]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Rename collection</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!name.trim() || !node) return;
            setSubmitting(true);
            await onSubmit(node, name.trim());
            setSubmitting(false);
            onOpenChange(false);
          }}
          className="flex flex-col gap-4"
        >
          <Input
            placeholder="Collection name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            disabled={submitting}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || submitting}>
              {submitting ? "Saving…" : "Save"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const AddTextDialog = ({
  open,
  onOpenChange,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (title: string, content: string) => void;
}) => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setTitle(""); setContent(""); setSubmitting(false); } onOpenChange(v); }}>
      <DialogContent className="flex max-h-[90vh] w-[90vw] max-w-4xl flex-col gap-4">
        <DialogHeader>
          <DialogTitle>Add Text</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!title.trim() || !content.trim()) return;
            setSubmitting(true);
            onSubmit(title.trim(), content.trim());
          }}
          className="flex min-h-0 flex-1 flex-col gap-4"
        >
          <Input
            placeholder="Title (becomes the filename)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
            disabled={submitting}
          />
          <Textarea
            placeholder="Paste or type your text here…"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            disabled={submitting}
            className="min-h-[50vh] flex-1 resize-none font-mono text-sm"
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={!title.trim() || !content.trim() || submitting}>
              {submitting ? "Saving…" : "Add to Library"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
